import {
  getAuth,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "firebase/auth";
import firebase from "firebase/compat";
import {
  authUiConfig,
  authProvider,
} from "../../../config/config.fireabse.authui";

import role from "../../../config/App-role";
import { createDebug } from "../../../common/utils";

const fireAuth = {};

fireAuth.isLoggedIn = (user) => {
  const isLogin = JSON.parse(localStorage.getItem("isLogin"));
  if (isLogin) return true;
  const keyRequired = ["uid", "email", "currentUser"];
  // eslint-disable-next-line no-underscore-dangle
  let _isLoggedIn = false;
  if (!user) return _isLoggedIn;
  keyRequired.forEach((key) => {
    if (Object.keys(user).indexOf(key) >= 0) _isLoggedIn = true;
  });
  return _isLoggedIn;
};

fireAuth.isAdmin = (user) => {
  const { role: userRole } = user;
  const isLoggedIn = fireAuth.isLoggedIn(user);
  // todo:更嚴謹的驗證方式,檢查 token
  return isLoggedIn && role && userRole === role.admin;
};

// 是否為團隊 admin
fireAuth.isGroupAdmin = (groupInfo) => groupInfo?.role === role.admin;

fireAuth.isRootAdmin = (user) => {
  const { role: userRole } = user;
  const isLoggedIn = fireAuth.isLoggedIn(user);
  // todo:更嚴謹的驗證方式,檢查 token
  return isLoggedIn && role && userRole === role.root;
};

fireAuth.getFormatUser = (user) => {
  // uid:
  // The user's ID, unique to the Firebase project. Do NOT use
  // this value to authenticate with your backend server, if
  // you have one. Use User.getToken() instead.
  const {
    uid,
    displayName,
    email,
    emailVerified,
    isAnonymous,
    providerData,
    metadata,
  } = user;

  const safeProviderId =
    providerData && providerData[0]
      ? providerData && providerData[0].providerId
      : "unKnown";

  // creationTime 或 lastSignInTime 可能為 undefined
  const { creationTime, lastSignInTime } = metadata;

  // make data flat
  return {
    // ...user,
    uid,
    displayName,
    email,
    emailVerified,
    isAnonymous,
    providerId: safeProviderId,
    creationTime,
    lastSignInTime,
  };
};

fireAuth.createUser = ({
  email,
  password,
  displayName,
  onSuccess,
  onError,
}) => {
  const auth = getAuth();

  createUserWithEmailAndPassword(auth, email, password)
    .then((userCredential) => {
      // 註冊後即進入登入狀態
      // Signed in
      const { user } = userCredential;
      // ...
      updateProfile(auth.currentUser, {
        displayName,
      })
        .then(() => {
          // Profile updated!
          // ...
          if (onSuccess) onSuccess({ user });
        })
        .catch((error) => {
          // An error occurred
          // ...
        });
    })
    .catch((error) => {
      // Handle Errors here.
      const errorCode = error.code;
      const errorMessage = error.message;
      // An error happened.
      if (onError) onError({ errorCode, errorMessage });
    });
};

fireAuth.signIn = ({ email, password, onSuccess, onError }) => {
  const auth = getAuth();

  signInWithEmailAndPassword(auth, email, password)
    .then((userCredential) => {
      // Signed in
      const { user } = userCredential;
      if (onSuccess) onSuccess({ user });
    })
    .catch((error) => {
      // Handle Errors here.
      const errorCode = error.code;
      const errorMessage = error.message;
      // An error happened.
      if (onError) onError({ errorCode, errorMessage });
    });
};

fireAuth.signOut = ({ onSuccess, onError }) => {
  const auth = getAuth();

  signOut(auth)
    .then(() => {
      // Sign-out successful.
      if (onSuccess) onSuccess();
    })
    .catch((error) => {
      // Handle Errors here.
      const errorCode = error.code;
      const errorMessage = error.message;
      // An error happened.
      if (onError) onError({ errorCode, errorMessage });
    });
};

const provider = new GoogleAuthProvider();

fireAuth.signInGoogle = ({ onSuccess, onError }) => {
  const auth = getAuth();

  signInWithPopup(auth, provider)
    .then((result) => {
      // This gives you a Google Access Token. You can use it to access the Google API.
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const token = credential.accessToken;
      // The signed-in user info.
      const { user } = result;
      // Sign-out successful.
      if (onSuccess) onSuccess({ user, token });
    })
    .catch((error) => {
      // Handle Errors here.
      const errorCode = error.code;
      const errorMessage = error.message;
      // The email of the user's account used.
      const { email } = error;
      // The AuthCredential type that was used.
      const credential = GoogleAuthProvider.credentialFromError(error);
      // An error happened.
      if (onError) onError({ errorCode, errorMessage, email, credential });
    });
};

// Send a user a verification email
fireAuth.sendEmailVerification = ({ onSuccess, onError }) => {
  const auth = getAuth();
  sendEmailVerification(auth.currentUser)
    .then(() => {
      // eslint-disable-next-line no-console
      console.log("Email verification sent!");
      // Email verification sent!
      // ...
      if (onSuccess) onSuccess();
    })
    .catch((error) => {
      const errorCode = error.code;
      const errorMessage = error.message;
      // An error happened.
      if (onError) onError({ errorCode, errorMessage });
    });
};

// Send a password reset email
fireAuth.sendPasswordResetEmail = ({ email, onSuccess, onError }) => {
  const auth = getAuth();

  sendPasswordResetEmail(auth, email)
    .then(() => {
      // Password reset email sent!
      // ..
      if (onSuccess) onSuccess();
    })
    .catch((error) => {
      const errorCode = error.code;
      const errorMessage = error.message;
      // An error happened.
      if (onError) onError({ errorCode, errorMessage });
    });
};

// 取得 firebase auth 物件
fireAuth.getAuth = () => getAuth();

// 取得設定檔
fireAuth.getAuthUiConfig = () => ({
  ...authUiConfig,
  // 這邊不設定 signInSuccessUrl, 讓使用者可以彈性地回到前一個頁面
  signInSuccessUrl: null,
  signInOptions: authProvider.map((ap) => ({
    provider: firebase.auth[ap].PROVIDER_ID,
  })),
  callbacks: {
    signInSuccessWithAuthResult: (authResult, redirectUrl) => true,
    signInFailure: (error) => {
      console.log("error", error);
    },
  },
});

export default fireAuth;
